<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="messages"
      class="elevation-1"
    >
      <template #top>
        <div class="d-flex align-center w-100">
          <v-spacer />
          <v-btn color="primary" class="mb-2" @click="openCreate">Ný skilaboð</v-btn>
        </div>
      </template>

      <template #item.link="{ item }: { item: MessageItem }">
        <span v-html="item.link || ''"></span>
      </template>

      <template #item.from="{ item }: { item: MessageItem }">
        <span>{{ fmtDate(item.from) }}</span>
      </template>

      <template #item.to="{ item }: { item: MessageItem }">
        <span>{{ fmtDate(item.to) }}</span>
      </template>

      <template #item.action="{ item }: { item: MessageItem }">
        <v-btn size="small" variant="text" @click="editItem(item)">Breyta</v-btn>
        <v-btn size="small" variant="text" @click="deleteItem(item)">Eyða</v-btn>
      </template>
    </v-data-table>

    <!-- Create / Edit dialog -->
    <v-dialog v-model="dialog" max-width="640">
      <v-card>
        <v-card-title class="text-h6">{{ formTitle }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                :model-value="dateInput(editedItem.from)"
                label="Frá"
                type="date"
                @update:model-value="(v:string)=>editedItem.from = parseDateInput(v)"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                :model-value="dateInput(editedItem.to)"
                label="Til"
                type="date"
                @update:model-value="(v:string)=>editedItem.to = parseDateInput(v)"
              />
            </v-col>
          </v-row>

          <v-text-field v-model="editedItem.title" label="Titill" />
          <v-textarea v-model="editedItem.body" label="Efni" auto-grow />

          <v-row v-if="editedItem.link">
            <v-col>
              <div class="boxclose" @click="removeLink" />
              <p v-html="editedItem.link"></p>
            </v-col>
          </v-row>

          <v-btn variant="text" @click.stop="linkDialog = true">Hlekkur</v-btn>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="close">Hætta við</v-btn>
          <v-btn variant="text" @click="save">Vista</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Link dialog -->
    <v-dialog v-model="linkDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Hlekkur</v-card-title>
        <v-card-text>
          <v-text-field v-model="linkName" label="Nafn á hlekki" />
          <v-text-field v-model="link" label="Hlekkur" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeLink">Hætta við</v-btn>
          <v-btn variant="text" @click="saveLink">Vista</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.open" :color="snackbar.color" :timeout="snackbar.timeout" location="top right">
      {{ snackbar.text }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAdminStore } from '@/stores/useAdminStore'

type MessageItem = {
  id?: number|string
  from: Date | string
  to: Date | string
  title: string
  body: string
  link?: string | null
}

const admin = useAdminStore()
const { messages } = storeToRefs(admin)
const { setMessage, addMessage, updateMessage, deleteMessage } = admin

onMounted(() => setMessage())

const headers = [
  { title: 'Frá', value: 'from' },
  { title: 'Til', value: 'to' },
  { title: 'Titill', value: 'title' },
  { title: 'Efni', value: 'body' },
  { title: 'Hlekkur', value: 'link' },
  { title: 'Aðgerðir', value: 'action', sortable: false },
]

const dialog = ref(false)
const linkDialog = ref(false)
const linkName = ref('')
const link = ref('')

const editedIndex = ref(-1)
const editedItem = ref<MessageItem>({
  from: new Date(),
  to: new Date(),
  title: '',
  body: '',
  link: '',
})

const defaultItem: MessageItem = {
  from: new Date(),
  to: new Date(),
  title: '',
  body: '',
  link: '',
}

const formTitle = computed(() => (editedIndex.value === -1 ? 'Ný skilaboð' : 'Breyta skilaboðum'))

function openCreate() {
  editedIndex.value = -1
  editedItem.value = { ...defaultItem }
  dialog.value = true
}

function editItem(item: MessageItem) {
  editedIndex.value = messages.value.findIndex(m => m === item)
  editedItem.value = {
    ...item,
    from: new Date(item.from),
    to: new Date(item.to),
  }
  dialog.value = true
}

async function deleteItem(item: MessageItem) {
  if (!confirm('Ertu viss um að þú viljir eyða?')) return
  try {
    await deleteMessage(item)
    await setMessage()
    ok('Vistað')
  } catch {
    err('Villa kom upp vinsamlegast endurhlaða vafra')
  }
}

function close() {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = { ...defaultItem }
    editedIndex.value = -1
  }, 200)
}

function closeLink() {
  linkDialog.value = false
  setTimeout(() => {
    linkName.value = ''
    link.value = ''
  }, 200)
}

function saveLink() {
  editedItem.value.link = `<a href="${link.value}" class="adminLink">${linkName.value}</a>`
  closeLink()
}

function removeLink() {
  editedItem.value.link = null
}

function parseDateInput(v: string): Date {
  // yyyy-mm-dd -> Date
  const [y, m, d] = v.split('-').map(n => Number(n))
  return new Date(y, (m || 1) - 1, d || 1)
}

function dateInput(v: string | Date): string {
  const d = v instanceof Date ? v : new Date(v)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function fmtDate(v: string | Date): string {
  const d = v instanceof Date ? v : new Date(v)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

async function save() {
  const payload: MessageItem = {
    ...editedItem.value,
    // ensure Date instances when saving
    from: new Date(editedItem.value.from),
    to: new Date(editedItem.value.to),
  }

  try {
    if (payload.id) {
      await updateMessage(payload)
    } else {
      await addMessage(payload)
    }
    await setMessage()
    ok('Vistað')
  } catch {
    err('Villa kom upp vinsamlegast endurhlaða vafra')
  }
  close()
}

// snackbar helpers
const snackbar = ref({ open: false, text: 'vistað', timeout: 3000, color: 'success' as 'success'|'error' })
function ok(text: string) { snackbar.value = { open: true, text, timeout: 3000, color: 'success' } }
function err(text: string) { snackbar.value = { open: true, text, timeout: 4000, color: 'error' } }
</script>

<style scoped lang="scss">
.v-card { max-height: 80vh; }
.boxclose {
  float: left;
  cursor: pointer;
  color: #fff;
  border: 1px solid #aeaeae;
  border-radius: 50%;
  background: #1976d2;
  font-size: 22px;
  font-weight: bold;
  line-height: 0px;
  padding: 8px 2px;
}
.boxclose:before { content: "×"; }
</style>
